import { Field, FieldError, FieldGroup, FieldSet } from "@components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@components/ui/input-group";
import { Label } from "@components/ui/label";
import { Sparkles } from "lucide-react";
import InfoTooltip from "@components/ui/info-tooltip";
import HeadlinesForm from "./HeadlinesForm";
import DescriptionsForm from "./DescriptionsForm";
import { useStageInit } from "@hooks/useStageInit";
import { useCallback, useEffect, useEffectEvent } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePage } from "@inertiajs/react";
import { z } from "zod";
import { useDebouncedSave } from "@hooks/useDebouncedSave";
import { useFormRegistration } from "@hooks/useFormRegistration";
import { useAdGroupNameStore } from "@stores/adGroupNameStore";
import type { CampaignProps } from "@components/ads/workflow-panel/workflow-buddy/ad-campaign.types";

const adGroupNameSchema = z.object({
  adGroupName: z.string().min(1, "Ad group name is required"),
});

type AdGroupNameFormData = z.infer<typeof adGroupNameSchema>;

export default function ContentForm() {
  useStageInit("content");

  const { campaign, ad_group } = usePage<CampaignProps>().props;
  const adGroupName = (ad_group as { name?: string } | null)?.name ?? "";
  const { values, setValues, hydrateOnce } = useAdGroupNameStore();

  const methods = useForm<AdGroupNameFormData>({
    resolver: zodResolver(adGroupNameSchema),
    mode: "onChange",
    defaultValues: values,
  });

  const hydrate = useEffectEvent(() => {
    const newValues = { adGroupName };
    if (hydrateOnce(newValues)) {
      methods.reset(newValues);
    }
  });

  useEffect(() => {
    hydrate();
  }, [ad_group && (ad_group as { id?: number }).id]);

  const { save, cancel } = useDebouncedSave({ campaignId: campaign?.id });

  useEffect(() => {
    const subscription = methods.watch((formValues, { type }) => {
      setValues(formValues as AdGroupNameFormData);
      if (type !== "change") return;
      const name = formValues.adGroupName?.trim();
      if (!name) return;
      save({ ad_group: { name } });
    });
    return () => subscription.unsubscribe();
  }, [methods, setValues, save]);

  const getData = useCallback((): Record<string, unknown> | null => {
    const name = methods.getValues().adGroupName?.trim();
    if (!name) return null;
    return { ad_group: { name } };
  }, [methods]);

  useFormRegistration("content-ad-group-name", methods, getData, cancel);

  return (
    <div className="border border-neutral-300 border-t-0 rounded-b-2xl bg-white" data-testid="content-form">
      <div className="py-8 pl-9 pr-[97px] flex flex-col gap-6">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-lg font-semibold leading-[22px]">Content</h2>
          <p className="text-xs text-base-300">
            Headlines and details are what people see in your Google Ad. We've drafted these from
            your business info — edit anything that doesn't sound like you.
          </p>
        </div>
        <FieldSet className="gap-4">
          <FieldGroup className="w-[405px]">
            <Field>
              <Label className="flex items-center gap-2 text-base-600">
                <span className="font-semibold">Ad Group Name</span>
                <InfoTooltip text="Ad groups organize your ads and keywords around a common theme." />
              </Label>
              <Controller
                name="adGroupName"
                control={methods.control}
                render={({ field }) => (
                  <InputGroup>
                    <InputGroupInput {...field} placeholder="Ad Group Name" />
                    <InputGroupAddon align="inline-end">
                      <Sparkles />
                    </InputGroupAddon>
                  </InputGroup>
                )}
              />
              <FieldError errors={[{ message: methods.formState.errors.adGroupName?.message }]} />
            </Field>
          </FieldGroup>
          <HeadlinesForm />
          <DescriptionsForm />
        </FieldSet>
      </div>
    </div>
  );
}
